import React from 'react';
import { Typography, Box, Chip } from '@mui/material';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import { ReviewTypeBadge } from '../../common/ReviewTypeBadge';
import { COLORS } from '../../../constants/radar';
import { 
  getReviewTypeColor, 
  getReviewTypeTextColor, 
  getReviewTypeText 
} from '../../../constants/reviewTypes';
import { isSessionClosedWithIncomplete } from '../../../constants/feedbackSessionStatuses';
import { SessionStatusChip } from '../../common/SessionStatusChip';

const EmployeeHeader = ({ reportData }) => {
  return (
    <Box flex={{ xs: '1', md: '5' }} sx={{ mb: { xs: 2, md: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <EmployeeAvatar employee={reportData.employee} size={48} sx={{ mr: 2 }} />
        <Box>
          <Typography variant="h6" color={COLORS.PRIMARY} gutterBottom>
            {reportData.employee?.fullName || 'N/A'}
          </Typography>
          <Typography sx={{ 
            fontSize: '14px', 
            color: '#64748B',
            lineHeight: 1.3,
            mt: -1
          }}>
            {reportData.employee?.orgPositions?.label || 'No Position'} 
            {reportData.employee?.orgGrades?.label && 
              <> • {reportData.employee.orgGrades.label}</>
            }
          </Typography>
        </Box>
      </Box>
      
      {/* ADR-014 §10j: the badge sits on the session line and describes the
          SESSION, not this individual — it shows whenever the parent session was
          closed early, even if this person's own assignment had finished. That
          keeps every report from one closed session telling the same story.
          `getAssignmentReport` includes the whole session row, so closureType /
          closureReason are already here; closureSummary is not, so the tooltip
          carries the description and the reason without the counts. */}
      {/* mb reproduces the gutterBottom the Typography used to carry on its own,
          so wrapping it in a flex row does not change the vertical rhythm. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: '0.35em' }}>
        <Typography variant="body1">
          {reportData.feedbackSession?.name
            ? `Feedback Session: ${reportData.feedbackSession.name} (${reportData.feedbackSession.month || 'N/A'}/${reportData.feedbackSession.year || 'N/A'})`
            : 'Individual Assignment'}
        </Typography>
        {isSessionClosedWithIncomplete(reportData.feedbackSession) && (
          <SessionStatusChip
            session={reportData.feedbackSession}
            sx={{ height: '22px', fontSize: '11px', '& .MuiChip-label': { px: 1 } }}
          />
        )}
      </Box>
      
      {reportData.reviewProcess?.[0]?.skillsMatrixSession && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Typography variant="body2" gutterBottom sx={{ mb: 0 }}>
            Matrix Session: {reportData.reviewProcess[0].skillsMatrixSession.name || 'N/A'}
          </Typography>
          {reportData.reviewProcess[0].skillsMatrixSession.version && (
            <Chip 
              label={`v${reportData.reviewProcess[0].skillsMatrixSession.version}`}
              size="small"
              sx={{ 
                fontSize: '11px',
                height: '20px',
                backgroundColor: '#F1F5F9',
                color: '#475569',
                border: '1px solid #E2E8F0',
                fontWeight: 500,
              }}
            />
          )}
        </Box>
      )}
      
      {reportData.reviewProcess?.[0]?.skillsMatrixSession?.reviewType && (
        <Box sx={{ mt: 1, mb: 2, display: 'inline-block' }}>
          <ReviewTypeBadge 
            reviewType={reportData.reviewProcess[0].skillsMatrixSession.reviewType}
            getReviewTypeColor={getReviewTypeColor}
            getReviewTypeTextColor={getReviewTypeTextColor}
            getReviewTypeText={getReviewTypeText}
          />
        </Box>
      )}
      
      <Typography variant="body2" gutterBottom>
        Skills Matrix: {reportData.skillsMatrix?.name || 'N/A'}
      </Typography>
      
      {reportData.skillsMatrix?.position && (
        <Typography variant="body2" gutterBottom>
          Position: {reportData.skillsMatrix.position.label || 'N/A'}
        </Typography>
      )}
      
      {reportData.skillsMatrix?.matrixRoles && reportData.skillsMatrix.matrixRoles.length > 0 && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Roles:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mt: 0.5, mb: 1 }}>
            {reportData.skillsMatrix.matrixRoles.map((matrixRole, index) => (
              <Box component="li" key={matrixRole?.id || index} sx={{ mb: 0.5 }}>
                <Typography variant="body2">
                  {matrixRole?.role?.label || 'N/A'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Assigned: {new Date(reportData.assignedAt).toLocaleDateString()}
      </Typography>
      
      {reportData.completedAt && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Completed: {new Date(reportData.completedAt).toLocaleDateString()}
        </Typography>
      )}
    </Box>
  );
};

export default EmployeeHeader; 